import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/context';
import { STOCKS, fetchAllStockFundamentals } from '../utils/stockFundamentals';
import { getTrendIndicator } from '../utils/portfolioMetrics';
import './StockFundamentalsPage.css';

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const ratingLabel = (rating, t) => {
  if (rating === 'buy') return t('fundPage.ratingBuy');
  if (rating === 'hold') return t('fundPage.ratingHold');
  return t('fundPage.ratingWatch');
};

const fmtB = (value) => {
  if (!hasValue(value)) return '';
  return `$${Number(value).toFixed(1)}B`;
};

const fmtMoney = (value) => {
  if (!hasValue(value)) return '';
  return `$${Number(value).toFixed(2)}`;
};

const fmtMarketCap = (value) => {
  if (!hasValue(value)) return '';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(0)}B`;
  return `$${(value / 1e6).toFixed(0)}M`;
};

const fmtPct = (value) => {
  if (!hasValue(value)) return '';
  return `${Number(value).toFixed(2)}%`;
};

const trendArrow = (arrow) => {
  if (arrow === 'up') return '↑';
  if (arrow === 'down') return '↓';
  return '→';
};

function Metric({ label, value, tone }) {
  if (!hasValue(value)) return null;
  return (
    <div className="fund-card-metric">
      <span>{label}</span>
      <strong className={tone || ''}>{value}</strong>
    </div>
  );
}

export default function StockFundamentalsPage() {
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const [stockFunds, setStockFunds] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchAllStockFundamentals()
      .then((data) => {
        if (!cancelled) {
          setStockFunds(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStockFunds(null);
          setLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const cards = STOCKS
    .map((stock) => ({ stock, data: stockFunds?.[stock.ticker] }))
    .filter(({ data }) => data);

  return (
    <div className="fund-page">
      <div className="fund-page-header">
        <h1>{t('fundPage.title')}</h1>
        <p className="fund-page-desc">
          {lang === 'zh'
            ? '展示 SEC companyfacts 与 Yahoo quote 可获取的真实数据；抓不到的指标会自动隐藏。'
            : 'Shows real values from SEC companyfacts and Yahoo quote. Missing fields are hidden.'}
        </p>
      </div>

      {loading ? (
        <div className="fund-page-loading">{t('fundPage.loading')}</div>
      ) : !stockFunds || cards.length === 0 ? (
        <div className="fund-page-loading">{t('fundPage.error')}</div>
      ) : (
        <div className="fund-card-grid">
          {cards.map(({ stock, data }) => {
            const revenueTrend = getTrendIndicator(data.revenueGrowth);
            const marketTrend = getTrendIndicator(data.regularMarketChangePercent);
            const title = lang === 'zh' ? stock.nameCn : stock.name;

            return (
              <article
                key={stock.ticker}
                className="fund-card"
                onClick={() => navigate(`/stock/${stock.ticker}`)}
              >
                <div className="fund-card-head">
                  <div className="fund-card-company">
                    <img src={stock.logo} alt="" className="fund-page-logo" />
                    <div>
                      <h2>{title}</h2>
                      <span>{stock.ticker}</span>
                    </div>
                  </div>
                  <span className={`fund-page-rating fund-page-rating-${data.rating || 'watch'}`}>
                    {ratingLabel(data.rating, t)}
                  </span>
                </div>

                <div className="fund-card-primary">
                  <Metric label={t('fundPage.revenue')} value={fmtB(data.revenue)} />
                  <Metric
                    label={t('fundPage.netIncome')}
                    value={hasValue(data.netIncome) ? `${data.netIncome >= 0 ? '+' : ''}${fmtB(data.netIncome)}` : ''}
                    tone={data.netIncome < 0 ? 'negative' : 'positive'}
                  />
                  <Metric label={lang === 'zh' ? '市值' : 'Market Cap'} value={fmtMarketCap(data.marketCap)} />
                  <Metric label="P/E" value={hasValue(data.trailingPE) ? Number(data.trailingPE).toFixed(1) : ''} />
                </div>

                <div className="fund-card-secondary">
                  {revenueTrend.value && (
                    <span className={`fund-card-chip ${revenueTrend.className}`}>
                      {t('fundPage.growth')} {revenueTrend.value} {trendArrow(revenueTrend.arrow)}
                    </span>
                  )}
                  {marketTrend.value && (
                    <span className={`fund-card-chip ${marketTrend.className}`}>
                      {lang === 'zh' ? '股价' : 'Price'} {marketTrend.value} {trendArrow(marketTrend.arrow)}
                    </span>
                  )}
                  {hasValue(data.eps) && <span className="fund-card-chip">EPS {fmtMoney(data.eps)}</span>}
                  {hasValue(data.cashFlow) && <span className="fund-card-chip">{lang === 'zh' ? '现金流' : 'Cash Flow'} {fmtB(data.cashFlow)}</span>}
                  {hasValue(data.totalAssets) && <span className="fund-card-chip">{lang === 'zh' ? '资产' : 'Assets'} {fmtB(data.totalAssets)}</span>}
                  {hasValue(data.dividendYield) && <span className="fund-card-chip">{lang === 'zh' ? '股息率' : 'Dividend'} {fmtPct(data.dividendYield * 100)}</span>}
                </div>

                <div className="fund-card-footer">
                  {data.nextEarnings && <span>{lang === 'zh' ? '下一财报' : 'Next earnings'}: {data.nextEarnings}</span>}
                  <strong>{t('fundPage.viewDetail')} →</strong>
                </div>
              </article>
            );
          })}
        </div>
      )}

      <div className="fund-page-source">{t('fundPage.source')}</div>
    </div>
  );
}
